import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Package, Trash2, DollarSign, ScanLine } from 'lucide-react';
import { onsiteApiFetch } from '../config/api.js';
import type { OnsitePartUsed } from '../../../api/src/types/onsite-types.js';

interface PartForm {
  part_number: string;
  description: string;
  quantity: string;
  unit_cost: string;
  billable: boolean;
}

const EMPTY_FORM: PartForm = {
  part_number: '',
  description: '',
  quantity: '1',
  unit_cost: '',
  billable: true,
};

export default function PartsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<PartForm>(EMPTY_FORM);

  const { data, isLoading } = useQuery({
    queryKey: ['parts', id],
    queryFn: () => onsiteApiFetch<{ parts: OnsitePartUsed[] }>(
      `/api/v1/onsite/parts?job_id=${id}`
    ),
    enabled: !!id,
  });

  const addPart = useMutation({
    mutationFn: (part: Omit<OnsitePartUsed, 'id' | 'type' | 'org_id' | 'tech_id' | 'added_at'>) =>
      onsiteApiFetch('/api/v1/onsite/parts', {
        method: 'POST',
        body: JSON.stringify(part),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['parts', id] });
      setShowForm(false);
      setForm(EMPTY_FORM);
    },
  });

  const deletePart = useMutation({
    mutationFn: (partId: string) =>
      onsiteApiFetch(`/api/v1/onsite/parts/${partId}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['parts', id] }),
  });

  const parts = data?.parts ?? [];
  const totalCost = parts
    .filter(p => p.unit_cost != null)
    .reduce((sum, p) => sum + p.quantity * (p.unit_cost ?? 0), 0);
  const totalBillable = parts
    .filter(p => p.billable && p.unit_cost != null)
    .reduce((sum, p) => sum + p.quantity * (p.unit_cost ?? 0), 0);
  const billableCount = parts.filter(p => p.billable).length;
  const totalItems = parts.reduce((sum, p) => sum + p.quantity, 0);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!id || !form.part_number || !form.description) return;
    await addPart.mutateAsync({
      job_id: id,
      part_number: form.part_number,
      description: form.description,
      quantity: parseInt(form.quantity, 10) || 1,
      unit_cost: form.unit_cost ? parseFloat(form.unit_cost) : undefined,
      billable: form.billable,
    });
  }

  return (
    <div className="flex h-full flex-col p-4">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-foreground">Parts Used</h1>
          {totalBillable > 0 && (
            <p className="flex items-center gap-1 text-sm text-green-400">
              <DollarSign className="h-3.5 w-3.5" />
              ${totalBillable.toFixed(2)} billable
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(`/jobs/${id}`)}
            className="rounded-lg border border-border px-3 py-2 text-sm text-muted-foreground hover:bg-secondary"
          >
            Back
          </button>
          <button
            onClick={() => setShowForm(v => !v)}
            className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm text-muted-foreground hover:bg-secondary"
          >
            <Plus className="h-4 w-4" />
            Manual
          </button>
        </div>
      </div>

      {/* Scan & Sell button — prominent at top */}
      <button
        onClick={() => navigate(`/jobs/${id}/scan`)}
        className="mb-4 flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-4 text-base font-semibold text-foreground hover:bg-primary/90"
      >
        <ScanLine className="h-5 w-5" />
        Scan & Sell Item
      </button>

      {/* Add form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="mb-4 rounded-xl border border-border bg-card p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">Part # *</label>
              <input
                value={form.part_number}
                onChange={e => setForm(f => ({ ...f, part_number: e.target.value }))}
                placeholder="SKU / part number"
                required
                className="w-full rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">Qty *</label>
              <input
                type="number"
                value={form.quantity}
                onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))}
                min="1"
                required
                className="w-full rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none"
              />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs text-muted-foreground">Description *</label>
            <input
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Part description"
              required
              className="w-full rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">Unit Cost ($)</label>
              <input
                type="number"
                value={form.unit_cost}
                onChange={e => setForm(f => ({ ...f, unit_cost: e.target.value }))}
                placeholder="0.00"
                step="0.01"
                className="w-full rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none"
              />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm text-foreground">
                <input
                  type="checkbox"
                  checked={form.billable}
                  onChange={e => setForm(f => ({ ...f, billable: e.target.checked }))}
                  className="h-4 w-4 rounded"
                />
                Billable
              </label>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="flex-1 rounded-lg border border-border py-2 text-sm text-muted-foreground hover:bg-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={addPart.isPending}
              className="flex-1 rounded-lg bg-primary py-2 text-sm font-medium text-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              Add
            </button>
          </div>
        </form>
      )}

      {/* Parts list */}
      <div className="flex-1 overflow-y-auto space-y-2">
        {isLoading ? (
          <div className="flex justify-center py-8">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        ) : parts.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-12 text-muted-foreground">
            <Package className="h-10 w-10" />
            <p className="font-medium">No parts recorded yet</p>
            <p className="text-sm text-muted-foreground text-center max-w-xs">
              Scan inventory items or add parts manually to include them in the final invoice.
            </p>
          </div>
        ) : (
          <>
            {parts.map(part => (
              <div key={part.id} className="flex items-center gap-3 rounded-xl border border-border bg-card p-4">
                <Package className="h-5 w-5 shrink-0 text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-foreground">{part.description}</p>
                    {part.billable ? (
                      <span className="rounded-full bg-green-500/20 px-1.5 py-0.5 text-xs text-green-400">
                        Billable
                      </span>
                    ) : (
                      <span className="rounded-full bg-secondary px-1.5 py-0.5 text-xs text-muted-foreground">
                        Non-Billable
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {part.part_number} · qty {part.quantity}
                    {part.unit_cost != null && (
                      <span className="text-green-400 font-medium">
                        {' '}· ${(part.unit_cost * part.quantity).toFixed(2)}
                      </span>
                    )}
                  </p>
                </div>
                <button
                  onClick={() => deletePart.mutate(part.id)}
                  className="rounded p-1.5 text-muted-foreground hover:bg-red-500/10 hover:text-red-400"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}

            {/* Totals footer */}
            <div className="rounded-xl border border-border bg-card p-4 mt-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  Total Parts: {totalItems} item{totalItems !== 1 ? 's' : ''}, {billableCount} billable
                </span>
                <span className="font-bold text-green-400">
                  ${totalCost.toFixed(2)}
                </span>
              </div>
              {totalBillable !== totalCost && totalBillable > 0 && (
                <div className="flex items-center justify-between text-xs mt-1">
                  <span className="text-muted-foreground">Billable subtotal</span>
                  <span className="text-green-400">${totalBillable.toFixed(2)}</span>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
